/**
 * Нэвтрээгүй хэрэглэгчийн хязгаарууд (нэг төхөөрөмж, localStorage):
 *
 * - Хайлт: 4 цагт ялгаатай 3 хайлт. `/job/public/list/search`-ийн `type: 'all'`
 *   болон хуудаслалтаас бусад параметртэй хүсэлтийг хайлт гэж тооцно.
 *   Ижил хайлтыг (хуудаслалт, refetch) дахин тоолохгүй.
 * - Хуудаслалт: 3 дахь хуудаснаас эхлэн үргэлж нэвтрэхийг шаардана.
 * - Зар үзэх: 4 цагт ялгаатай 10 зар. Үзсэн зараа дахин нээж болно.
 *
 * 4 цаг нь тухайн цонхны эхний бүртгэлээс эхэлж тоологдоно.
 */

export const GUEST_LIMIT_WINDOW_MS = 4 * 60 * 60 * 1000;
export const GUEST_SEARCH_LIMIT = 3;
export const GUEST_JOB_VIEW_LIMIT = 10;
/** Нэвтрээгүй хэрэглэгч үзэж болох хамгийн сүүлийн хуудас. */
export const GUEST_MAX_PAGE = 2;

export const GUEST_LIMIT_MESSAGES = {
  search:
    'Та нэвтрэлгүйгээр хайлт хийх хязгаартаа хүрлээ. Хайлтаа үргэлжлүүлэхийн тулд нэвтэрнэ үү.',
  page: 'Дараагийн хуудсуудыг үзэхийн тулд нэвтэрнэ үү.',
  jobView:
    'Та нэвтрэлгүйгээр зар үзэх хязгаартаа хүрлээ. Зарыг үзэхийн тулд нэвтэрнэ үү.',
} as const;

type Params = Record<string, unknown> | null | undefined;

const PAGINATION_KEYS = new Set(['page', 'limit', 'size', 'pageSize']);

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** Хайлтын параметрээс тогтвортой түлхүүр гаргана. Тоологдох хайлт биш бол `null`. */
export function getSearchSignature(params: Params): string | null {
  if (!params) return null;

  const entries = Object.entries(params)
    .filter(
      ([key, value]) =>
        !PAGINATION_KEYS.has(key) &&
        !(key === 'type' && value === 'all') &&
        !isEmptyValue(value)
    )
    .map(([key, value]) => [key, String(value).trim()] as const)
    .sort(([a], [b]) => a.localeCompare(b));

  return entries.length > 0 ? JSON.stringify(entries) : null;
}

/** Нэвтрэх шаардлагатай хуудас бол түлхүүр, үгүй бол `null`. */
export function getRestrictedPageKey(params: Params): string | null {
  const page = Number(params?.page);
  return Number.isFinite(page) && page > GUEST_MAX_PAGE ? `page:${page}` : null;
}

export interface GuestLimiter {
  /** Түлхүүрийг бүртгэж, зөвшөөрөгдсөн эсэхийг буцаана. Бүртгэгдсэн түлхүүрийг дахин тоолохгүй. */
  consume: (key: string, now?: number) => boolean;
  reset: () => void;
}

interface LimiterState {
  windowStart: number;
  keys: string[];
}

/** `windowMs` хугацаанд `limit` ялгаатай түлхүүр зөвшөөрөх localStorage хязгаарлагч. */
export function createGuestLimiter({
  storageKey,
  limit,
  windowMs = GUEST_LIMIT_WINDOW_MS,
}: {
  storageKey: string;
  limit: number;
  windowMs?: number;
}): GuestLimiter {
  const read = (now: number): LimiterState | null => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return null;
      const { windowStart, keys } = JSON.parse(raw) as Partial<LimiterState>;
      const isValid =
        typeof windowStart === 'number' &&
        Array.isArray(keys) &&
        windowStart <= now &&
        now - windowStart < windowMs;
      return isValid
        ? { windowStart, keys: keys.filter((k) => typeof k === 'string') }
        : null;
    } catch {
      return null;
    }
  };

  const write = (state: LimiterState) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // localStorage боломжгүй (private mode г.м.) — хязгаарлахгүй.
    }
  };

  return {
    consume(key, now = Date.now()) {
      const state = read(now) ?? { windowStart: now, keys: [] };
      if (state.keys.includes(key)) return true;
      if (state.keys.length >= limit) return false;
      write({ ...state, keys: [...state.keys, key] });
      return true;
    },
    reset() {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
    },
  };
}

export const guestSearchLimiter = createGuestLimiter({
  storageKey: 'zangia:guest-search-limit',
  limit: GUEST_SEARCH_LIMIT,
});

export const guestJobViewLimiter = createGuestLimiter({
  storageKey: 'zangia:guest-job-view-limit',
  limit: GUEST_JOB_VIEW_LIMIT,
});

/** Үргэлж нэвтрэхийг шаардана (хуудаслалт). */
export const loginRequiredLimiter: GuestLimiter = {
  consume: () => false,
  reset: () => {},
};
